import { cvCompetencesAPI, cvCertificatesAPI, cvCoursesAPI, cvEducationsAPI, CvSubAPI } from 'api/CvAPI'
import { orderBy } from 'lodash'
import React, { useEffect, useState } from 'react'
import { ICourse, ICv, IEducation, IPersonCertificate, IPersonCompetence } from 'types/cvsInterfaces'
import withProfileCard from 'Components/reusable/HOC/withProfileCard'
import { Data, IItemDraftHandle } from 'types/hocTypes'
import {
  getCertificatesInitialData,
  getCompetencesInitialData,
  getCourseInitialData,
  getEducationInitialData,
} from 'Components/reusable/DataContext/InitialData'
import {
  certificateSchema,
  competenceSchema,
  courseSchema,
  educationSchema,
} from 'Components/reusable/DataContext/ValidationSchema'
import EducationItemCard from './EducationItemCard'
import EducationModal from './EducationModal'
import { ObjectSchema } from 'yup'
import { NewOrExisting } from 'Components/reusable/DataContext'
import { useUser } from 'hooks'

/** @notExported */
interface IEducationCardProps {
  /** Is card editable. */
  editable: boolean
  /** CV object. */
  cv: ICv
  /** Function to set updated at. */
  setUpdatedAt: () => void
  /** Replace or add data function. */
  replaceOrAddData: (
    newItem:
      | IEducation
      | ICourse
      | IPersonCertificate
      | IPersonCompetence
      | { deleted: number; item: IEducation | ICourse | IPersonCertificate | IPersonCompetence },
    type: 'education' | 'courses' | 'certificates' | 'competences'
  ) => Data
  /** Function to open item. */
  openItem: (item: IEducation | ICourse | IPersonCertificate | IPersonCompetence) => void
  /** Edited item. */
  editedItem: IEducation | null
  /** Function to close item. */
  closeItem: () => void
  /** Header. */
  header: string
  /** Data object. */
  data: {
    education: IEducation[]
    courses: ICourse[]
    certificates: IPersonCertificate[]
    competences: IPersonCompetence[]
  }
  /** Function to update overview data. */
  updateOverviewData: (type: string, data: unknown | unknown[]) => void
  /** Is item anonymous. */
  anonymous?: boolean
  /** Function to update progress indicator. */
  updateProgress: () => void
  /** Dialog drafts from the CV wizard (see `withProfileCard`'s `itemDraft`). */
  itemDraft?: IItemDraftHandle
  /** Function to update education list */
  setEducation: (employers: IEducation[]) => void
  /** Function to update course list */
  setCourses: (employers: ICourse[]) => void
  /** Function to update certificate list */
  setCertificates: (certificates: IPersonCertificate[]) => void
  /** Function to update competence list */
  setCompetences: (competences: IPersonCompetence[]) => void
}

/**
 * Education card.
 *
 * @returns Education card component.
 * @notExported
 */
const EducationCard: React.FC<IEducationCardProps> = ({
  cv,
  editable,
  setUpdatedAt,
  closeItem,
  replaceOrAddData,
  openItem,
  editedItem,
  header,
  data,
  updateOverviewData,
  anonymous = false,
  updateProgress,
  itemDraft,
  setEducation,
  setCourses,
  setCertificates,
  setCompetences,
}) => {
  const { features } = useUser()
  const [type, setType] = useState<string>('degrees')
  const [api, setApi] = useState<
    CvSubAPI<IPersonCompetence> | CvSubAPI<IPersonCertificate> | CvSubAPI<IEducation> | CvSubAPI<ICourse>
  >()
  const [schema, setSchema] = useState<ObjectSchema>()
  const [initialData, setInitialData] =
    useState<NewOrExisting<IEducation | ICourse | IPersonCertificate | IPersonCompetence>>()
  const [localeBase, setLocaleBase] = useState<string>()

  useEffect(() => {
    ;(async () => {
      setLocaleBase('profile.education.modal')
      setSchema(educationSchema())
      setInitialData(await getEducationInitialData(cv.id))
      setApi(cvEducationsAPI)
    })()
  }, [cv.id])

  useEffect(() => {
    ;(async () => {
      if (editedItem && type) {
        if (type === 'degrees') {
          setLocaleBase('profile.education.modal')
          setSchema(educationSchema())
          setInitialData(await getEducationInitialData(cv.id))
          setApi(cvEducationsAPI)
        } else if (type === 'courses') {
          setLocaleBase('profile.course.modal')
          setSchema(courseSchema())
          setInitialData(await getCourseInitialData(cv.id))
          setApi(cvCoursesAPI)
        } else if (type === 'certificates') {
          setLocaleBase('profile.certificate.modal')
          setSchema(certificateSchema())
          setInitialData(await getCertificatesInitialData(cv.id))
          setApi(cvCertificatesAPI)
        } else if (type === 'competences' && features.includes('competences')) {
          setLocaleBase('profile.competence.modal')
          setSchema(competenceSchema())
          setInitialData(await getCompetencesInitialData(cv.id))
          setApi(cvCompetencesAPI)
        }
      }
    })()
  }, [cv.id, features, type, editedItem])

  const checkType = (
    item:
      | IEducation
      | ICourse
      | IPersonCertificate
      | IPersonCompetence
      | { deleted: number; item: IEducation | ICourse | IPersonCertificate | IPersonCompetence }
  ): 'education' | 'courses' | 'certificates' | 'competences' | undefined => {
    const data = 'deleted' in item ? item.item : item

    if ('degree' in data.translations[0]) {
      return 'education'
    } else if ('course' in data.translations[0]) {
      return 'courses'
    } else if ('Certificate' in data) {
      return 'certificates'
    } else if ('Competence' in data && features.includes('competences')) {
      return 'competences'
    }
  }

  return (
    <>
      <EducationItemCard
        color="success"
        title="degrees"
        educations={orderBy(data.education, ['endDate', 'startDate'], ['desc', 'desc'])}
        editable={editable}
        openItem={openItem}
        anonymous={anonymous}
      />
      <EducationItemCard
        color="warning"
        title="courses"
        courses={orderBy(data.courses, ['endDate', 'startDate'], ['desc', 'desc'])}
        editable={editable}
        openItem={openItem}
        anonymous={anonymous}
      />
      <EducationItemCard
        color="info"
        title="certificates"
        certificates={orderBy(data.certificates, ['endDate', 'issueDate'], ['desc', 'desc'])}
        editable={editable}
        openItem={openItem}
        anonymous={anonymous}
      />
      {features.includes('competences') && (
        <EducationItemCard
          color="info"
          title="competences"
          competences={orderBy(data.competences, ['endDate', 'issueDate'], ['desc', 'desc'])}
          editable={editable}
          openItem={openItem}
          anonymous={anonymous}
        />
      )}
      {editedItem !== undefined && (
        <EducationModal
          key={editedItem?.id ?? 'new'}
          cvId={cv.id}
          item={editedItem}
          draft={itemDraft?.forItem(editedItem ?? null)}
          localeBase={localeBase}
          schema={schema}
          initialData={initialData}
          maxWidth="md"
          fullWidth={true}
          api={api}
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as
              | IEducation
              | ICourse
              | IPersonCertificate
              | IPersonCompetence
              | { deleted: number; item: IEducation | ICourse | IPersonCertificate | IPersonCompetence }
              | undefined
            closeItem()
            if (newItem) {
              setUpdatedAt()
              const type = checkType(newItem)
              if (type) {
                const data = replaceOrAddData(newItem, type)
                updateOverviewData(type, data)
                updateProgress()
                setEducation(data.education)
                setCourses(data.courses)
                setCertificates(data.certificates)
                if (features.includes('competences')) setCompetences(data.competences)
              }
            }
          }}
          header={header}
          noEscClose
          setType={setType}
        />
      )}
    </>
  )
}

export default withProfileCard(EducationCard as unknown as Parameters<typeof withProfileCard>[0])
